// Alış Faturası / Sipariş / Teklif formu kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'documents.purchaseInvoice';

export const purchaseInvoiceGuide = (t: TFunction): GuideContent => ({
  layout: 'stacked',
  title: t(`${G}.title`, 'Alış belgesi nasıl hazırlanır?'),
  intro: t(`${G}.intro`, 'Alış faturası, alış siparişi ve alış teklifi aynı formla hazırlanır. Kaydettiğinizde stok ve tedarikçi carisine etkisi belge türüne göre değişir. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık ve düğmeler'),
    form: t(`${G}.regions.form`, 'Cari ve belge bilgileri'),
    list: t(`${G}.regions.list`, 'Kalemler'),
    footer: t(`${G}.regions.footer`, 'Notlar ve toplamlar'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Belge türünü ve düğmeleri tanıyın'),
      text: t(`${G}.s1.text`, 'Başlık hangi belgede olduğunuzu gösterir: "Yeni Alış Faturası", "Yeni Alış Siparişi" veya "Yeni Alış Teklifi". Belge türü formu açtığınız yerden gelir (Faturalar, Siparişler veya Teklifler sayfası).'),
      tips: [
        t(`${G}.s1.tip1`, 'Taslak Kaydet: formu yalnız bu tarayıcıda saklar; stok ve cariye dokunmaz. Form doluyken taslak ayrıca kendiliğinden kaydedilir; formu yeniden açınca "Kaydedilmiş Taslak Bulundu" penceresi sorar.'),
        t(`${G}.s1.tip2`, 'Kaydet / Güncelle: belgeyi kalıcı olarak kaydeder (etkileri 6. adımda).'),
        t(`${G}.s1.tip3`, 'Proje düğmesiyle belgeyi bir projeye bağlayabilirsiniz (isteğe bağlı).'),
      ],
    },
    {
      region: 'form',
      title: t(`${G}.s2.title`, '2. Tedarikçiyi seçin'),
      text: t(`${G}.s2.text`, '"Cari Seç" ile tedarikçiyi seçin; ünvan, yetkili kişi, vergi no, vergi dairesi, adres ve telefon forma gelir ve bu belge için düzeltilebilir. Başka bir cariye geçmek için "Farklı Cari Seç"e basın.'),
    },
    {
      region: 'form',
      title: t(`${G}.s3.title`, '3. Belge bilgileri, şube ve gider türü'),
      text: t(`${G}.s3.text`, 'Belge numarası boş bırakılırsa otomatik oluşturulur. Tarih, Vade Tarihi, Para Birimi ve Döviz Kuru girin; döviz kuru 0\'dan büyük olmalıdır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Şube (yalnız faturada): faturanın hangi şubeye ait olduğunu belirler; "Merkez (şubesiz)" de seçilebilir. Şube müdürü olarak girdiyseniz kendi şubenize kilitlidir.'),
        t(`${G}.s3.tip2`, 'Seçilen şubeye bağlı depo yoksa depo alanları gizlenir ve fatura stok hareketi oluşturmadan kaydedilir.'),
        t(`${G}.s3.tip3`, 'Gider türü (opsiyonel, yalnız faturada): boş bırakılırsa alım stok / mal alımı (maliyet) sayılır.'),
        t(`${G}.s3.tip4`, 'Teklifte Geçerlilik Tarihi ve Teklif Durumu, siparişte Teslim Tarihi ve Sipariş Durumu alanları görünür.'),
      ],
    },
    {
      region: 'list',
      title: t(`${G}.s4.title`, '4. Kalemleri ve depoyu girin'),
      text: t(`${G}.s4.text`, '"Ürün Ekle" stoktan ürün seçer, "Stoksuz Ürün Ekle" adını elle yazdığınız satır ekler, "Başka Faturadan" başka bir belgenin kalemlerini kopyalar, "PDF\'den Yükle" tedarikçinin PDF faturasındaki ürünleri okur. Stoklu her satırın sonunda depo seçin veya "Toplu Depo Seç" ile hepsine aynı depoyu verin.'),
      tips: [
        t(`${G}.s4.tip1`, 'Her satırda Miktar, Fiyatı, KDV (Dahil / Hariç) ve KDV % girin. Miktar 0\'dan büyük, KDV ve iskonto oranı 0–100 arasında olmalı; ürün adı ve birim zorunludur. Hatalı satırlar kaydetmeden önce tek tek listelenir.'),
        t(`${G}.s4.tip2`, 'İndirim sütununun başlığından yüzde (İndirim %) veya tutar seçilir; seçim tüm kalemler için geçerlidir.'),
        t(`${G}.s4.tip3`, 'Teklifte miktarı 0 olan kalem varsa kaydetmeden önce onayınız istenir.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s5.title`, '5. Toplamları ve orijinal fatura tutarını kontrol edin'),
      text: t(`${G}.s5.text`, 'Toplamlar bölümünde Ara Toplam, varsa İndirim, her KDV oranı için ayrı KDV satırı, KDV Toplam, varsa ÖTV Toplam ve Genel Toplam görünür. + düğmesiyle Tevkifat veya Stopaj ekleyebilirsiniz.'),
      tips: [
        t(`${G}.s5.tip1`, '"Orijinal Fatura Genel Toplamı" alanına tedarikçi faturasındaki toplamı yazarsanız aradaki yuvarlama farkı "Fatura Farkı" olarak görünür ve Genel Toplam bu tutara eşitlenir. Cariye bu tutar işlenir.'),
      ],
    },
    {
      region: 'header',
      title: t(`${G}.s6.title`, '6. Kaydedin: stok ve cariye etkisi'),
      text: t(`${G}.s6.text`, 'Kaydet için cari ve en az bir kalem gerekir. Belgenin etkisi türüne göre değişir:'),
      tips: [
        t(`${G}.s6.tip1`, 'Alış faturası: seçilen depoya stok girişi yapılır ve tedarikçinin alacağı artar. Cariye işlenen tutar (girildiyse orijinal fatura toplamı) tevkifat / stopaj düşülerek bulunur ve TL\'ye çevrilir. Fatura, stok ve cari birlikte kaydedilir; biri başarısız olursa hiçbiri yazılmaz.'),
        t(`${G}.s6.tip2`, 'Ürünün alış fiyatı bu faturadaki birim maliyetle güncellenir: şubeli faturada şube alış fiyatı, şubesiz faturada ürün kartındaki alış fiyatı. İade faturasında güncellenmez.'),
        t(`${G}.s6.tip3`, 'İade tipli alış faturası ters çalışır: mal stoktan çıkar, tedarikçinin alacağı azalır. Orijinal alış faturasının seçilmesi zorunludur.'),
        t(`${G}.s6.tip4`, 'Stoksuz ürün satırları ve deposu seçilmemiş satırlar stok hareketi oluşturmaz.'),
        t(`${G}.s6.tip5`, 'Alış siparişi: kaydederken "Sipariş kaydedilirken stoğa kaydedilsin mi?" diye sorulur. "Evet, Stoğa Kaydedilsin" ürünleri stoğa ekler ve geri alınamaz. Sipariş cari bakiyesini değiştirmez. Düzenlemede bu soru sorulmaz.'),
        t(`${G}.s6.tip6`, 'Alış teklifi stok ve cari bakiyesini değiştirmez; yalnız belge kaydedilir.'),
        t(`${G}.s6.tip7`, 'Güncelle: eski stok ve cari hareketleri geri alınır, yeni hareketler yazılır.'),
      ],
    },
  ],
});
